import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Warning, CheckCircle, Buildings, Stack, ArrowLeft } from "@phosphor-icons/react";
import { MODULE_ORDER } from "@/context/ModulesContext";

export default function BulkModulesModal({ open, onClose, onDone }) {
  const [orgs, setOrgs] = useState([]);
  const [labels, setLabels] = useState({});
  const [module, setModule] = useState("prepayments");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setSelected(new Set()); setConfirming(false); setModule("prepayments");
    api.get("/entity/modules/portfolio").then(({ data }) => {
      setOrgs(data.organisations || []);
      setLabels(data.labels || {});
    }).catch(() => toast.error("Could not load organisations")).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const eligible = orgs.filter((o) => !o.modules?.[module]);          // module currently OFF
  const already = orgs.length - eligible.length;

  const chooseModule = (m) => { setModule(m); setSelected(new Set()); };
  const toggle = (tid) => setSelected((s) => { const n = new Set(s); n.has(tid) ? n.delete(tid) : n.add(tid); return n; });
  const allSel = eligible.length > 0 && eligible.every((o) => selected.has(o.tenant_id));
  const selectAll = () => setSelected(allSel ? new Set() : new Set(eligible.map((o) => o.tenant_id)));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/entity/modules/bulk-enable", { module, tenant_ids: [...selected] });
      toast.success(`${labels[module] || module} switched on for ${data.count} organisation${data.count === 1 ? "" : "s"}`);
      window.dispatchEvent(new Event("modules:refresh"));
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch the module on");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bulk-modules-overlay"
      onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col" data-testid="bulk-modules-modal"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl bg-orange/15 flex items-center justify-center shrink-0">
              <Stack size={20} weight="fill" style={{ color: "#F37A24" }} />
            </span>
            <div>
              <h3 className="font-heading font-bold text-navy text-lg">Switch on a module across clients</h3>
              <p className="text-xs text-navy/70 mt-0.5">Turn one ledger on for several organisations at once. Modules can never be switched off.</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} data-testid="bulk-modules-close" className="text-navy/40 hover:text-navy transition-colors"><X size={20} /></button>
        </div>

        {loading ? (
          <div className="p-10 text-center font-mono text-navy/60 text-sm tracking-widest">LOADING…</div>
        ) : !confirming ? (
          <>
            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <div>
                <span className="overline text-navy/70 block mb-1.5">Module to switch on</span>
                <div className="flex flex-wrap gap-2" data-testid="bulk-module-picker">
                  {MODULE_ORDER.map((m) => (
                    <button key={m} onClick={() => chooseModule(m)} data-testid={`bulk-module-${m}`}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${module === m ? "border-orange bg-orange/10 text-orangeink" : "border-slate-200 text-navy/70 hover:border-orange/50"}`}
                      style={module === m ? { borderColor: "#F37A24", color: "#B4560F" } : undefined}>
                      {labels[m] || m}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="overline text-navy/70">Organisations with {labels[module]} off</span>
                  {eligible.length > 0 && (
                    <button onClick={selectAll} data-testid="bulk-select-all" className="text-xs font-semibold text-orangeink hover:underline">
                      {allSel ? "Clear all" : "Select all"}
                    </button>
                  )}
                </div>
                {eligible.length === 0 ? (
                  <div className="text-sm text-navy/70 py-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="bulk-none-eligible">
                    {labels[module]} is already switched on for every connected organisation.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="bulk-org-list">
                    {eligible.map((o) => (
                      <label key={o.tenant_id} data-testid={`bulk-org-${o.tenant_id}`}
                        className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={selected.has(o.tenant_id)} onChange={() => toggle(o.tenant_id)}
                          data-testid={`bulk-org-check-${o.tenant_id}`} className="accent-orange w-4 h-4" />
                        <Buildings size={15} className="text-slate-400 shrink-0" />
                        <span className="text-sm text-navy truncate">{o.tenant_name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {already > 0 && (
                  <p className="text-xs text-navy/55 mt-2" data-testid="bulk-already-on">
                    {already} organisation{already === 1 ? "" : "s"} already {already === 1 ? "has" : "have"} {labels[module]} on.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button onClick={onClose} data-testid="bulk-cancel" className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
              <button onClick={() => setConfirming(true)} disabled={selected.size === 0} data-testid="bulk-continue"
                className="px-5 py-2 text-sm font-semibold rounded-lg text-white hover:bg-navy transition-colors disabled:opacity-50" style={{ background: "#F37A24" }}>
                Switch on for {selected.size} selected
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="px-6 py-5 overflow-y-auto">
              <div className="flex items-start gap-3 rounded-xl border border-orange/30 bg-orange/[0.04] p-4">
                <Warning size={22} weight="fill" style={{ color: "#F37A24" }} className="shrink-0" />
                <div>
                  <div className="font-semibold text-navy text-sm">Switch on {labels[module]} for {selected.size} organisation{selected.size === 1 ? "" : "s"}?</div>
                  <p className="text-sm text-navy/70 mt-1.5">
                    This turns on the <span className="font-semibold text-navy">{labels[module]}</span> ledger for the selected organisations.
                    It <span className="font-semibold text-navy">cannot be switched off later</span> — the module posts real journals to Xero.
                  </p>
                </div>
              </div>
              <div className="mt-4 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-52 overflow-y-auto" data-testid="bulk-confirm-list">
                {orgs.filter((o) => selected.has(o.tenant_id)).map((o) => (
                  <div key={o.tenant_id} className="flex items-center gap-2 px-4 py-2 text-sm text-navy">
                    <CheckCircle size={15} weight="fill" style={{ color: "#00A859" }} /> {o.tenant_name}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between gap-2 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setConfirming(false)} disabled={busy} data-testid="bulk-back"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 disabled:opacity-60">
                <ArrowLeft size={14} /> Back
              </button>
              <button onClick={submit} disabled={busy} data-testid="bulk-confirm-enable"
                className="px-5 py-2 text-sm font-semibold rounded-lg text-white hover:bg-navy transition-colors disabled:opacity-60" style={{ background: "#F37A24" }}>
                {busy ? "Switching on…" : `Yes, switch on for ${selected.size}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
